import { useState } from 'react'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { Button } from '@klear/ui/components/button'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import type { DocumentReviewReason } from '@klear/schema/document-processing'
import { ArrivalProvenance } from '@/app/document-review/email-provenance'
import { reviewReasonLabel } from '@klear/schema/document-review-step'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

export type BlockedOffer =
  | { kind: 'reprocessWithTrader' }
  | { kind: 'reprocess' }
  | { kind: 'keyInByHand' }
  | { kind: 'notImportWork' }
  | { kind: 'nonDocument' }

export type BlockedReading = {
  title: string
  detail: string
  offer: BlockedOffer
}

export const OFFER_BY_REASON: Record<DocumentReviewReason, BlockedOffer['kind'] | null> = {
  trader_unresolved: 'reprocessWithTrader',
  too_large: 'keyInByHand',
  unsupported_format: 'keyInByHand',
  no_text_layer: 'keyInByHand',
  preview_failed: 'keyInByHand',
  model_failed: 'reprocess',
  low_confidence: null
}

const DETAIL_BY_OFFER: Record<BlockedOffer['kind'], string> = {
  reprocessWithTrader:
    'Which side of the deal the trader is on decides the record kind, so a run that named the wrong party ' +
    'reasoned about it from the first line. Name the trader and process the document again rather than ' +
    'relabelling this plan.',
  keyInByHand: 'Nothing here can be processed by machine. Leave the review open for a person to key in by hand.',
  reprocess:
    'Process the document again. If a second run says the same, leave the review open for a person to key in by hand.',
  notImportWork: "Keep it among the trader's documents, process it again, or dismiss it.",
  nonDocument: 'Dismiss it and Klear skips this file wherever it arrives again.'
}

export function readBlock(detail: DocumentReviewDetailOutput): BlockedReading | null {
  const nonDocument = detail.proposals.some((proposal) =>
    proposal.doubts.some((doubt) => doubt.kind === 'non_document')
  )
  if (nonDocument && detail.reviewReason !== 'trader_unresolved') {
    return {
      title: 'This is not a document',
      detail: DETAIL_BY_OFFER.nonDocument,
      offer: { kind: 'nonDocument' }
    }
  }

  const notImportWork = detail.proposals.some((proposal) =>
    proposal.doubts.some((doubt) => doubt.kind === 'not_import_work')
  )
  if (notImportWork && detail.reviewReason !== 'trader_unresolved') {
    return {
      title: 'This may not be import work at all',
      detail: DETAIL_BY_OFFER.notImportWork,
      offer: { kind: 'notImportWork' }
    }
  }

  const reason = detail.reviewReason
  const offered = reason === null ? null : OFFER_BY_REASON[reason]
  if (reason !== null && offered !== null) {
    return { title: reviewReasonLabel(reason), detail: DETAIL_BY_OFFER[offered], offer: { kind: offered } }
  }

  if (detail.proposals.length === 0) {
    return {
      title: reason === null ? 'The run proposed nothing' : reviewReasonLabel(reason),
      detail: DETAIL_BY_OFFER.reprocess,
      offer: { kind: 'reprocess' }
    }
  }

  if (detail.proposals.every((proposal) => proposal.record === null && proposal.kind !== 'trader_document')) {
    return {
      title: 'The run only asked a question',
      detail:
        'Every proposal asks for something rather than describing a record, so there is nothing to create. ' +
        'Answer what it asks and process the document again, or leave it for a person to key in.',
      offer: { kind: 'reprocess' }
    }
  }

  return null
}

export type BlockedPanelProps = {
  reading: BlockedReading
  arrival: Pick<DocumentReviewDetailOutput, 'email' | 'sentBy' | 'arrivedAt'>
  traders: readonly { id: number; name: string | null }[]
  canEdit: boolean
  isProcessing: boolean
  isFiling: boolean
  onReprocess: (traderId?: number) => void
  onFileAsUpload: () => void
  onReject: () => void
}

export function BlockedPanel({
  reading,
  arrival,
  traders,
  canEdit,
  isProcessing,
  isFiling,
  onReprocess,
  onFileAsUpload,
  onReject
}: BlockedPanelProps) {
  const [traderId, setTraderId] = useState('')

  const traderOptions: ComboboxOption[] = traders.map((trader) => ({
    value: toString(trader.id),
    label: trader.name ?? `Trader ${trader.id}`
  }))

  return (
    <div className="bg-card rounded-lg border">
      <div className="p-4">
        <h2 className="text-base font-medium">{reading.title}</h2>
        <p className="text-muted-foreground mt-1 text-sm">{reading.detail}</p>

        {!canEdit ? (
          <p className="text-muted-foreground mt-3 text-sm">
            Acting on this needs source-document edit. Ask a super admin or a CSM to take it.
          </p>
        ) : reading.offer.kind === 'reprocessWithTrader' ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label>Trader</Label>
              <Combobox
                options={traderOptions}
                value={traderId}
                onChange={setTraderId}
                triggerLabel="Select trader"
                placeholder="Search traders…"
                className="w-[220px]"
                size="sm"
              />
            </div>
            <Button
              size="sm"
              disabled={traderId === '' || isProcessing}
              onClick={() => onReprocess(toNumber(traderId))}
            >
              {isProcessing ? 'Queueing…' : 'Process for this trader'}
            </Button>
            <Button variant="outline" size="sm" onClick={onReject}>
              Dismiss it
            </Button>
          </div>
        ) : reading.offer.kind === 'reprocess' ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={isProcessing} onClick={() => onReprocess()}>
              {isProcessing ? 'Queueing…' : 'Process again'}
            </Button>
          </div>
        ) : reading.offer.kind === 'nonDocument' ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={isProcessing} onClick={onReject}>
              Dismiss it
            </Button>
            <Button variant="outline" size="sm" disabled={isProcessing} onClick={() => onReprocess()}>
              {isProcessing ? 'Queueing…' : 'Process again'}
            </Button>
          </div>
        ) : reading.offer.kind === 'keyInByHand' ? null : (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={isFiling || isProcessing} onClick={onFileAsUpload}>
              {isFiling ? 'Filing…' : 'File as an upload'}
            </Button>
            <Button variant="outline" size="sm" disabled={isFiling || isProcessing} onClick={() => onReprocess()}>
              {isProcessing ? 'Queueing…' : 'Process again'}
            </Button>
            <Button variant="outline" size="sm" disabled={isFiling || isProcessing} onClick={onReject}>
              Dismiss it
            </Button>
          </div>
        )}
      </div>
      <ArrivalProvenance arrival={arrival} />
    </div>
  )
}
